import { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  useTheme,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { getBlogPost } from '../../config/blog-posts.data';

export const formatPostDate = (isoDate) =>
  new Date(`${isoDate}T12:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

// Página de um post. O texto (HTML vindo do antigo WordPress) é carregado
// de public/blog/posts/<slug>.json só quando a pessoa abre o post.
const BlogPost = () => {
  const theme = useTheme();
  const { slug } = useParams();
  const post = getBlogPost(slug);
  const [html, setHtml] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!post) return;
    setHtml(null);
    setFailed(false);
    document.title = `${post.title} | Blog Assegurados`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute('content', post.excerpt);
    // Ignora a resposta se a pessoa já foi para outro post.
    let cancelled = false;
    fetch(`/blog/posts/${slug}.json`)
      .then((res) => res.json())
      .then((data) => !cancelled && setHtml(data.html))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [post, slug]);

  if (!post) {
    return (
      <Container maxWidth="md" sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          Post não encontrado
        </Typography>
        <Button component={RouterLink} to="/blog" variant="contained">
          Ver todos os posts
        </Button>
      </Container>
    );
  }

  return (
    <Box>
      {/* Hero */}
      <Box sx={{ backgroundColor: theme.palette.text.primary, color: 'white' }}>
        <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
          <Button
            component={RouterLink}
            to="/blog"
            startIcon={<ArrowBack />}
            sx={{ color: theme.palette.grey[100], mb: 2, px: 0 }}
          >
            Blog
          </Button>
          <Typography variant="h1" sx={{ fontSize: { xs: '1.9rem', md: '2.6rem' }, mb: 1.5 }}>
            {post.title}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.grey[100] }}>
            {formatPostDate(post.date)}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {post.image && (
          <Box
            component="img"
            src={post.image}
            alt={post.title}
            sx={{ width: '100%', borderRadius: theme.spacingValues.medium, mb: 4 }}
          />
        )}

        {failed && (
          <Typography sx={{ color: theme.palette.text.secondary }}>
            Não foi possível carregar este post. Tente recarregar a página.
          </Typography>
        )}

        {!html && !failed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {html && (
          <Box
            // Conteúdo fixo, migrado do nosso próprio WordPress.
            dangerouslySetInnerHTML={{ __html: html }}
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '1.05rem',
              lineHeight: 1.8,
              overflowWrap: 'break-word',
              '& h2': {
                ...theme.typography.h2,
                fontSize: { xs: '1.45rem', md: '1.7rem' },
                color: theme.palette.text.primary,
                mt: 5,
                mb: 1.5,
              },
              '& h3': {
                ...theme.typography.h3,
                fontSize: { xs: '1.2rem', md: '1.35rem' },
                color: theme.palette.text.primary,
                mt: 4,
                mb: 1,
              },
              '& p': { my: 1.5 },
              '& ul, & ol': { pl: 3, my: 1.5 },
              '& li': { mb: 0.75 },
              '& a': { color: theme.palette.primary.main },
              '& strong': { color: theme.palette.text.primary },
              '& figure': { m: 0, my: 3 },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                mx: 'auto',
                borderRadius: theme.spacingValues.small,
              },
              '& figcaption': {
                fontSize: '0.85rem',
                textAlign: 'center',
                mt: 1,
              },
            }}
          />
        )}

        {/* Chamada para cotação */}
        <Box
          sx={{
            mt: 6,
            p: { xs: 3, md: 4 },
            borderRadius: theme.spacingValues.medium,
            backgroundColor: theme.palette.grey[50],
            textAlign: 'center',
          }}
        >
          <Typography variant="h3" sx={{ mb: 1 }}>
            Vai viajar? Viaje protegido.
          </Typography>
          <Typography sx={{ color: theme.palette.text.secondary, mb: 2.5 }}>
            Compare os planos da Assist Card, Universal Assistance e Affinity e
            contrate em poucos minutos.
          </Typography>
          <Button
            component={RouterLink}
            to="/"
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
          >
            Cotar seguro viagem
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default BlogPost;
